import { DateTime } from "luxon";
import ExcludedDate from "../interfaces/robot/excluded-dates/excluded-date";
import { getLabeledMonthForMonthIndex } from "./month";
import { getShortLabeledWeekdayForWeekdayIndex } from "./weekday";
import { getCurrentLanguage } from "../i18n";
import TimeSpan from "./time-span";
import i18n from "../i18n";
import { replaceArgsInMessage } from "./strings";

export const MILLIS_IN_A_DAY = 86400000

export const formatAndTranslateDate = (date: DateTime, includeYear: boolean = true) => {
  const dateNumber = date.day.toString();
  const day = getShortLabeledWeekdayForWeekdayIndex(date.weekday);
  const month = getLabeledMonthForMonthIndex(date.month);
  const year = date.year.toString();
  return `${day} ${dateNumber} ${month} ${includeYear ? year : ""}`;
};

export const isExcludedDateWithReason = (dayIndex: number, noGoDates: ExcludedDate[] | null, roomId?: string) => {
  const today = DateTime.now();
  const checkDate = today.minus({ days: today.weekday - dayIndex });
  if (noGoDates == null) return false;
  return noGoDates.find(
    (d) =>
      isExcludedDateActive(d, checkDate) && (d.roomIds.length === 0 || (roomId != null && d.roomIds.includes(roomId)))
  )?.reason;
};

export const isExcludedDateActive = (excludedDate: ExcludedDate, date?: DateTime) => {
  let today = date ?? DateTime.now();
  const startDate = DateTime.fromJSDate(new Date(excludedDate.startDate));
  const endDate = DateTime.fromJSDate(new Date(excludedDate.endDate));

  if (excludedDate.repeatAnnually) {
    today = today.set({ year: startDate.year });
  }

  return isDateInRange(startDate, today, endDate);
};

export const isDateInRange = (startDate: DateTime, checkDate: DateTime, endDate: DateTime) => {
  return (
    (startDate <= checkDate && checkDate <= endDate) ||
    startDate.startOf("day").toMillis() === checkDate.startOf("day").toMillis() ||
    endDate.startOf("day").toMillis() === checkDate.startOf("day").toMillis()
  );
};

export const toDateString = (date: DateTime) => {
  return date.toFormat("yyyy-MM-dd");
};

export const toDateTimeString = (date: DateTime) => {
  return date.toFormat("yyyy-MM-dd_HH-mm-ss");
};

export const parseDateString = (date: string) => {
  return DateTime.fromFormat(date, "yyyy-MM-dd");
};

export const getDateTimeFormatted = (date: DateTime, format: string = "dd.MM.yyyy, HH:mm:ss") => {
  date = date.setLocale(getCurrentLanguage().languageCode);
  return date.toFormat(format);
};

export const getTimeFormatted = (date: DateTime, includeSeconds: boolean = false) => {
  return date.toFormat(`HH:mm${includeSeconds ? ":ss" : ""}`);
};

export const timespanToDateTime = (timespan: TimeSpan) => {
  return DateTime.fromObject({ minute: timespan.minutes, hour: timespan.hours });
};

export const dateTimeToTimespan = (date: DateTime) => {
  return {
    hours: date.hour,
    minutes: date.minute,
  } as TimeSpan;
};

export const convertToDateTime = (date: string | Date | DateTime) => {
  if (date instanceof Date) {
    return DateTime.fromJSDate(date, { zone: "utc" });
  } else if (typeof date === "string") {
    return DateTime.fromISO(date, { zone: "utc" });
  }
  return date.setZone("utc");
};

export const getTimeBetweenDates = (dateA: string | Date | DateTime, dateB: string | Date | DateTime) => {
  const parsedDateA = convertToDateTime(dateA);
  const parsedDateB = convertToDateTime(dateB);

  return parsedDateA.toMillis() - parsedDateB.toMillis();
};

export const dateToStringFormat = (date: DateTime | Date | string, shortFormat?: boolean, timezone: string = "utc") => {
  let unformattedDate = convertToDateTime(date);
  if (timezone != null) {
    if (timezone === "GMT Standard Time") {
      timezone = "Europe/London";
    }
    unformattedDate = unformattedDate.setZone(timezone);
  }
  if (shortFormat) {
    return unformattedDate.toFormat("dd/MM/yy");
  }
  return unformattedDate.toFormat("dd LLL yyyy - HH:mm");
};

export const sortByDate = (dateA: DateTime | Date | string, dateB: DateTime | Date | string) => {
  const parsedDateA = convertToDateTime(dateA);
  const parsedDateB = convertToDateTime(dateB);

  return parsedDateB.toMillis() - parsedDateA.toMillis();
};

export const convertSecondsToRoundedString = (seconds: number, showSeconds?: boolean, showZeroMins = true) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  
  if (hours === 0 && minutes === 0) {
    return showSeconds === true
      ? `${seconds} ${i18n.t("label-seconds-short")}`
      : `< 1 ${i18n.t("label-minutes-short")}`;
  } else if (hours === 0) {
    return `${minutes} ${i18n.t("label-minutes-short")}`;
  } else if (minutes === 0 && !showZeroMins) {
    return `${hours} ${i18n.t("label-hours-short")}`;
  }

  return `${hours} ${i18n.t("label-hours-short")} ${minutes} ${i18n.t("label-minutes-short")}`;
};

export const convertSecondsToHourMinSecString = (seconds: number) => {
  const hours = Math.floor(seconds / 3600);
  seconds = seconds - hours * 3600;
  const minutes = Math.floor(seconds / 60);
  seconds = seconds - minutes * 60;

  return (
    `${hours.toString().padStart(2, "0")}:` +
    `${minutes.toString().padStart(2, "0")}:` +
    `${seconds.toString().padStart(2, "0")}`
  );
};

export const dateToTimeSinceString = (date: DateTime | Date | string) => {
  const parsedDate = convertToDateTime(date);
  const now = DateTime.now();
  const secondsPassed = Math.max((now.toMillis() - parsedDate.toMillis()) / 1000, 0);

  if (secondsPassed < 60) {
    return replaceArgsInMessage(i18n.t("label-seconds-ago"), [secondsPassed.toFixed(0)]);
  }
  const minutesPassed = secondsPassed / 60;
  if (minutesPassed < 60) {
    return replaceArgsInMessage(i18n.t("label-minutes-ago"), [minutesPassed.toFixed(0)]);
  }
  const hoursPassed = minutesPassed / 60;
  if (hoursPassed < 24) {
    return replaceArgsInMessage(i18n.t("label-hours-ago"), [hoursPassed.toFixed(0)]);
  }
  const daysPassed = hoursPassed / 24;
  return replaceArgsInMessage(i18n.t("label-days-ago"), [daysPassed.toFixed(0)]);
};

export const areDatesEqual = (a: DateTime | Date | string | null, b: DateTime | Date | string | null) => {
  if (a == null || b == null) return false;
  const parsedA = convertToDateTime(a);
  const parsedB = convertToDateTime(b);

  return parsedA.toMillis() === parsedB.toMillis();
};
